import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  COLLECTION_PROTECTION_STATUSES,
  COLLECTION_STATUSES,
  SUPPRESS_BILLS_VALUES,
} from '@app/shared';
import type { CustomerDto, Paged, SettingsRecordDto } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { Can } from '../auth/guards';
import { Field } from './parts';

/**
 * The customer page dialogs read on the walk of 2026-10-23 (release switch
 * `customer-account-dialogs`): Update plan custom expiry date, Update Customer Classification,
 * Collection Protection Status, Suppress Bills and the winback notification.
 */

const fieldError = (err: unknown): string | null =>
  err instanceof ApiError ? (err.details[0]?.message ?? null) : null;

/** A PUT of one of the dialogs that refreshes the customer. */
function useAccountChange<T>(customerId: string, path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => api.put<CustomerDto>(`/customers/${customerId}/${path}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}

/** A dialog with one field, its error under it, Submit / Update and Close. */
function OneFieldDialog({
  open,
  title,
  submitLabel,
  busy,
  onSubmit,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  busy: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <FormDialog
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      submitting={busy}
      submitLabel={submitLabel}
      cancelLabel="Close"
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>{children}</Box>
    </FormDialog>
  );
}

/** "Update plan custom expiry date": an edit icon beside Plan Expiry Date. */
export function PlanCustomExpiry({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useAccountChange<{ date: string }>(customer.id, 'plan-custom-expiry');
  if (!customer.line.plan) return null;
  const submit = async () => {
    if (!date) return setProblem('Select Date');
    try {
      await save.mutateAsync({ date });
      notify.success('Plan expiry date updated.');
      setOpen(false);
    } catch (err) {
      const message = fieldError(err);
      if (message) setProblem(message);
      else notify.error(err);
    }
  };
  return (
    <Can code="PC377">
      <Tooltip title="Update plan custom expiry date">
        <IconButton
          size="small"
          aria-label="Update plan custom expiry date"
          onClick={() => {
            setDate(customer.line.planExpiresAt?.slice(0, 10) ?? '');
            setProblem(null);
            setOpen(true);
          }}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <OneFieldDialog
        open={open}
        title="Update plan custom expiry date"
        submitLabel="Update"
        busy={save.isPending}
        onSubmit={() => void submit()}
        onClose={() => setOpen(false)}
      >
        <TextField
          type="date"
          label="Select Date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setProblem(null);
          }}
          error={Boolean(problem)}
          helperText={problem}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </OneFieldDialog>
    </Can>
  );
}

/** "Customer Classification" row with "Update Customer Classification". */
export function ClassificationField({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useAccountChange<{ classificationId: string }>(customer.id, 'classification');
  const choices = useQuery({
    queryKey: ['settings-lists', 'customer-classifications', 'options'],
    queryFn: () =>
      api.get<Paged<SettingsRecordDto>>('/settings-lists/customer-classifications', {
        pageSize: 200,
        status: 'Active',
      }),
    select: (r) => r.data,
    enabled: open,
  });
  const flags = customer.accountFlags;
  const submit = async () => {
    if (!value) return setProblem('Please select a classification option.');
    try {
      await save.mutateAsync({ classificationId: value });
      notify.success('Customer classification updated.');
      setOpen(false);
    } catch (err) {
      const message = fieldError(err);
      if (message) setProblem(message);
      else notify.error(err);
    }
  };
  return (
    <Field
      label="Customer Classification"
      value={
        <>
          {flags?.classification ?? ''}
          <Can code="PC787">
            <Button
              size="small"
              onClick={() => {
                setValue(flags?.classificationId ?? '');
                setProblem(null);
                setOpen(true);
              }}
            >
              Update
            </Button>
            <OneFieldDialog
              open={open}
              title="Update Customer Classification"
              submitLabel="Update"
              busy={save.isPending}
              onSubmit={() => void submit()}
              onClose={() => setOpen(false)}
            >
              <TextField
                select
                label="Customer Classification"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setProblem(null);
                }}
                error={Boolean(problem)}
                helperText={problem}
              >
                <MenuItem value="">-- Select Classification --</MenuItem>
                {(choices.data ?? []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {String(r.values.name ?? '')}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2">
                <MuiLink component={Link} to="/settings/other/list/customer-classifications">
                  Manage Customer Classification
                </MuiLink>
              </Typography>
            </OneFieldDialog>
          </Can>
        </>
      }
    />
  );
}

/** "Collection Protection Status" or "Suppress Bills Status": the current value and its dialog. */
function ChoiceField<V extends string>({
  customer,
  label,
  title,
  changeLabel,
  pleaseSelect,
  current,
  choices,
  path,
  body,
  done,
}: {
  customer: CustomerDto;
  label: string;
  title: string;
  changeLabel: string;
  pleaseSelect: string;
  current: V;
  choices: readonly V[];
  path: string;
  body: (v: V) => Record<string, string>;
  done: string;
}) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<V | ''>('');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useAccountChange<Record<string, string>>(customer.id, path);
  const submit = async () => {
    if (!value) return setProblem(pleaseSelect);
    try {
      await save.mutateAsync(body(value));
      notify.success(done);
      setOpen(false);
    } catch (err) {
      const message = fieldError(err);
      if (message) setProblem(message);
      else notify.error(err);
    }
  };
  return (
    <Field
      label={label}
      value={
        <>
          {current}
          <Can code="PC377">
            <Button
              size="small"
              onClick={() => {
                setValue('');
                setProblem(null);
                setOpen(true);
              }}
            >
              Update
            </Button>
            <OneFieldDialog
              open={open}
              title={title}
              submitLabel="Submit"
              busy={save.isPending}
              onSubmit={() => void submit()}
              onClose={() => setOpen(false)}
            >
              <Typography variant="body2">
                Customer ID: {customer.customerId}
                <br />
                {label}: {current}
              </Typography>
              <TextField
                select
                label={changeLabel}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value as V);
                  setProblem(null);
                }}
                error={Boolean(problem)}
                helperText={problem}
              >
                <MenuItem value="">{pleaseSelect}</MenuItem>
                {choices.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </OneFieldDialog>
          </Can>
        </>
      }
    />
  );
}

export function CollectionProtectionField({ customer }: { customer: CustomerDto }) {
  return (
    <ChoiceField
      customer={customer}
      label="Collection Protection Status"
      title="Collection Protection Status"
      changeLabel="Change Collection Protection Status To"
      pleaseSelect="Please Select Collection Protection Status"
      current={customer.accountFlags?.collectionProtection ? 'Enable' : 'Disable'}
      choices={COLLECTION_PROTECTION_STATUSES}
      path="collection-protection"
      body={(status) => ({ status })}
      done="Collection Protection Status updated."
    />
  );
}

type CollectionStatus = (typeof COLLECTION_STATUSES)[number];

/** "Collection Status" (switch collection-status). */
export function CollectionStatusField({ customer }: { customer: CustomerDto }) {
  const current = (customer.accountFlags?.collectionStatus ?? 'None') as CollectionStatus;
  return (
    <ChoiceField
      customer={customer}
      label="Collection Status"
      title="Collection Status"
      changeLabel="Change Collection Status To"
      pleaseSelect="Please Select Collection Status"
      current={current}
      choices={COLLECTION_STATUSES}
      path="collection-status"
      body={(status) => ({ status })}
      done="Collection Status updated."
    />
  );
}

export function SuppressBillsField({ customer }: { customer: CustomerDto }) {
  return (
    <ChoiceField
      customer={customer}
      label="Suppress Bills Status"
      title="Suppress Bills"
      changeLabel="Suppress Bills To"
      pleaseSelect="Please Select Suppress Bills"
      current={customer.accountFlags?.suppressBills ? 'YES' : 'NO'}
      choices={SUPPRESS_BILLS_VALUES}
      path="suppress-bills"
      body={(value) => ({ value })}
      done="Suppress Bills Updated Successfully"
    />
  );
}

/** The winback notification of an Inactive account: the line's number or the alternate one. */
export function Winback({ customer }: { customer: CustomerDto }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState<'MDN' | 'ALTERNATE' | ''>('');
  const [sent, setSent] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (target: string) =>
      api.post<{ message: string }>(`/customers/${customer.id}/winback`, { to: target }),
  });
  if (customer.status !== 'Inactive') return null;
  const numbers: Array<['MDN' | 'ALTERNATE', string, string]> = [
    ...(customer.line.mdn
      ? [['MDN', customer.line.mdn, 'Telephone Number'] as ['MDN', string, string]]
      : []),
    ...(customer.alternateNumber
      ? [
          ['ALTERNATE', customer.alternateNumber, 'Alternate Number'] as [
            'ALTERNATE',
            string,
            string,
          ],
        ]
      : []),
  ];
  if (numbers.length === 0) return null;
  const submit = async () => {
    if (!to) return setProblem('Choose the mobile number');
    try {
      setSent((await send.mutateAsync(to)).message);
    } catch (err) {
      setProblem(fieldError(err) ?? (err instanceof Error ? err.message : 'Not sent'));
    }
  };
  const title =
    'Please choose the mobile number on which you want to send the winback notification.';
  return (
    <Can code="PC930">
      <Button
        variant="outlined"
        onClick={() => {
          setTo('');
          setSent(null);
          setProblem(null);
          setOpen(true);
        }}
      >
        Winback
      </Button>
      <OneFieldDialog
        open={open}
        title={title}
        submitLabel="Submit"
        busy={send.isPending || Boolean(sent)}
        onSubmit={() => void submit()}
        onClose={() => setOpen(false)}
      >
        {sent ? (
          <Alert severity="success">{sent}</Alert>
        ) : (
          <>
            <RadioGroup
              aria-label={title}
              value={to}
              onChange={(e) => {
                setTo(e.target.value as 'MDN' | 'ALTERNATE');
                setProblem(null);
              }}
            >
              {numbers.map(([key, number, what]) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio />}
                  label={`${number} (${what})`}
                />
              ))}
            </RadioGroup>
            {problem ? <Alert severity="error">{problem}</Alert> : null}
          </>
        )}
      </OneFieldDialog>
    </Can>
  );
}
